import { styled, alpha } from '@mui/material/styles';
import { Box, Paper, TableCell } from '@mui/material';

/**
 * ページコンテナ
 */
export const PageContainer = styled(Box)(({ theme }) => ({
  padding: 0,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

/**
 * ヘッダーセクション
 */
export const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

/**
 * ページヘッダー行（全ボタン横一列）
 */
export const PageHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
  padding: theme.spacing(1, 0),
}));

/**
 * 月ナビゲーションエリア
 */
export const MonthNavigation = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  '& .MuiIconButton-root': {
    width: 32,
    height: 32,
    border: '1px solid #d8d2cd',
    borderRadius: 7,
    backgroundColor: '#fff',
    color: '#514b47',
  },
}));

/**
 * ヘッダーボタングループ
 */
export const HeaderButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  width: '100%',
  '& .MuiButton-root': { whiteSpace: 'nowrap' },
}));

/**
 * タイトルエリア
 */
export const TitleArea = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

/**
 * アクションボタンエリア
 */
export const ActionButtonsArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

/**
 * カレンダーコンテナ
 */
export const CalendarContainer = styled(Paper)(({ theme }) => ({
  padding: 0,
  marginBottom: theme.spacing(3),
  maxWidth: '100%',
  overflowX: 'auto',
}));

/**
 * カレンダーヘッダー
 */
export const CalendarHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
}));

/**
 * カレンダーグリッド
 */
export const CalendarGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  gap: 1,
  backgroundColor: '#e8e4df',
  borderTop: '1px solid #e8e4df',
  '@media (max-width: 700px)': { minWidth: 560 },
}));

/**
 * 曜日ヘッダーセル
 */
export const WeekdayHeader = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{ $isSunday?: boolean; $isSaturday?: boolean }>(
  ({ theme, $isSunday, $isSaturday }) => ({
    padding: '9px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: '#f8f7f5',
    color: $isSunday
      ? theme.palette.error.main
      : $isSaturday
        ? '#637c9e'
        : theme.palette.text.primary,
  })
);

/**
 * 日付セル
 */
export const DayCell = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{
  $isSunday?: boolean;
  $isSaturday?: boolean;
  $isHoliday?: boolean;
  $isEmpty?: boolean;
}>(({ theme, $isSunday, $isSaturday, $isHoliday, $isEmpty }) => ({
  minHeight: 116,
  padding: 9,
  border: 0,
  backgroundColor: $isEmpty
    ? theme.palette.background.default
    : $isHoliday || $isSunday
      ? alpha(theme.palette.error.light, 0.1)
      : $isSaturday
        ? '#f3f6fa'
        : theme.palette.background.paper,
  cursor: $isEmpty ? 'default' : 'pointer',
  minWidth: 0,
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: $isEmpty
      ? theme.palette.background.default
      : alpha(theme.palette.primary.main, 0.1),
  },
}));

/**
 * 日付ラベル
 */
export const DateLabel = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{
  $isSunday?: boolean;
  $isSaturday?: boolean;
  $isHoliday?: boolean;
}>(({ theme, $isSunday, $isSaturday, $isHoliday }) => ({
  fontSize: 12,
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  color: $isHoliday || $isSunday
    ? theme.palette.error.main
    : $isSaturday
      ? '#637c9e'
      : theme.palette.text.primary,
}));

/**
 * 祝日ラベル
 */
export const HolidayLabel = styled(Box)(({ theme }) => ({
  fontSize: 9,
  color: theme.palette.error.main,
  marginBottom: theme.spacing(0.5),
}));

/**
 * 時間帯スロット表示
 */
export const TimeSlotItem = styled(Box)(({ theme }) => ({
  fontSize: 9,
  padding: '3px 4px',
  marginBottom: 3,
  backgroundColor: '#f0eeeb',
  borderRadius: 4,
  display: 'flex',
  justifyContent: 'space-between',
}));

/**
 * もっと見るボタン
 */
export const MoreButton = styled(Box)(({ theme }) => ({
  fontSize: 9,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  textAlign: 'center',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

/**
 * 編集可能テーブルセル（赤背景）
 */
export const EditableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.error.light, 0.15),
  padding: theme.spacing(0.5),
}));

/**
 * 表示専用テーブルセル
 */
export const ReadOnlyCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(0.5),
  textAlign: 'center',
}));

/**
 * ステータスチップスタイル
 */
export const StatusChipStyles = {
  draft: {
    backgroundColor: (theme: { palette: { grey: { 200: string } } }) =>
      theme.palette.grey[200],
    color: (theme: { palette: { text: { secondary: string } } }) =>
      theme.palette.text.secondary,
  },
  recruiting: {
    backgroundColor: (theme: { palette: { success: { light: string } } }) =>
      alpha(theme.palette.success.light, 0.3),
    color: (theme: { palette: { success: { dark: string } } }) =>
      theme.palette.success.dark,
  },
  closed: {
    backgroundColor: (theme: { palette: { warning: { light: string } } }) =>
      alpha(theme.palette.warning.light, 0.3),
    color: (theme: { palette: { warning: { dark: string } } }) =>
      theme.palette.warning.dark,
  },
  confirmed: {
    backgroundColor: (theme: { palette: { primary: { light: string } } }) =>
      alpha(theme.palette.primary.light, 0.3),
    color: (theme: { palette: { primary: { dark: string } } }) =>
      theme.palette.primary.dark,
  },
};

/**
 * エラーコンテナ
 */
export const ErrorContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

/**
 * ローディングオーバーレイ
 */
export const LoadingOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  zIndex: 10,
}));

/**
 * モーダルコンテンツ
 */
export const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[24],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  maxHeight: '90vh',
  overflow: 'auto',
  boxSizing: 'border-box',
  minWidth: 'min(400px, calc(100vw - 32px))',
  maxWidth: 'calc(100vw - 32px)',
}));

/**
 * ドラッグ&ドロップエリア
 */
export const DropZone = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{ $isDragActive?: boolean }>(
  ({ theme, $isDragActive }) => ({
    border: `2px dashed ${$isDragActive ? theme.palette.primary.main : theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    backgroundColor: $isDragActive
      ? alpha(theme.palette.primary.main, 0.05)
      : theme.palette.background.paper,
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
  })
);
